import { NavLink } from "react-router-dom";

import { SummaryCard } from "../components/SummaryCard";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";
import { DetailCardGrid, DetailMetricGrid } from "./DeepDiveParts";

export function UnderwritingPage() {
  const { data, loading, error } = useContent("underwriting-page", api.getUnderwriting);

  return (
    <PageContainer
      eyebrow="Deep Detail - Financials"
      title="Underwriting detail for the Financials surface"
      lede="This route is the scenario-detail layer beneath Financials. It keeps the modeling discipline, scenario posture, and limits available without presenting underwriting as a separate primary investor surface."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading underwriting summary...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? (
        <div className="stack-xl">
          <DetailMetricGrid
            items={[
              { title: "Core Position Items", value: `${data.corePosition.length}`, detail: "Current model posture" },
              { title: "Market Backdrop Inputs", value: `${data.marketBackdrop.length}`, detail: "Directional STR context" },
              { title: "Operating Frictions", value: `${data.operatingConsiderations.length}`, detail: "Costs and compliance items" },
              { title: "Status", value: data.status, detail: "Underwriting summary status" }
            ]}
          />

          <DetailCardGrid
            items={[
              ...data.corePosition.slice(0, 4).map((item) => ({
                eyebrow: "Core Position",
                title: item.topic,
                description: item.currentRead
              })),
              ...data.marketBackdrop.slice(0, 4).map((item) => ({
                eyebrow: "Market Backdrop",
                title: item.metric,
                description: item.currentRead,
                fields: [
                  { label: "Source Logic", value: item.sourceLogic },
                  { label: "Confidence", value: item.confidence }
                ]
              }))
            ]}
          />

          <div className="grid-two">
            <SummaryCard eyebrow="STR Case Support" title="Why the main case is modelable" description={data.overview}>
              <ul className="bullet-list">
                {data.strSupport.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard eyebrow="Fallback Support" title="Why the downside is not blank" description={data.currentBestRead}>
              <ul className="bullet-list">
                {data.fallbackSupport.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <div className="grid-two">
            <SummaryCard eyebrow="Operating Considerations" title="Recurring drag still matters" description="Small projects can still be weakened by taxes, compliance, and turnover friction.">
              <ul className="bullet-list">
                {data.operatingConsiderations.map((item) => (
                  <li key={item.operatingTopic} className="bullet-item">{`${item.operatingTopic}: ${item.currentRead}`}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard eyebrow="Discipline Rules" title="What the model should not overclaim" description="The point of the current pass is structured realism, not premature certainty.">
              <ul className="bullet-list">
                {data.disciplineRules.map((item) => (
                  <li key={item.rule} className="bullet-item">{`${item.rule}: ${item.whyItExists}`}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <SummaryCard eyebrow="Limits" title="Why this is still a feasibility-stage model" description={data.conclusion}>
            <ul className="bullet-list">
              {data.limits.map((item) => (
                <li key={item} className="bullet-item">{item}</li>
              ))}
            </ul>
          </SummaryCard>

          <SummaryCard
            eyebrow="Parent Surface"
            title="Return to Financials for the full investor-facing economics read"
            description="Use this page when the scenario discipline needs a deeper read. Use Financials for the integrated budget, operating, underwriting, fallback, and disclosure posture."
          >
            <div className="row-wrap gap-sm overview-go-deeper-actions">
              <NavLink to="/financials" className="link-button link-button-secondary">
                Review Financials
              </NavLink>
              <NavLink to="/fallback" className="link-button link-button-secondary">
                Open Fallback
              </NavLink>
            </div>
          </SummaryCard>
        </div>
      ) : null}
    </PageContainer>
  );
}
